import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import {
  securityService,
  type SecurityLogSource,
  type SecurityLogs,
  type SecurityStatus,
} from '@/services/sysadmin/securityService';
import { CrowdSecEventsTable } from './CrowdSecEventsTable';

const LOG_SOURCES: SecurityLogSource[] = ['fail2ban', 'ufw', 'crowdsec', 'lifeline'];

export default function SecurityPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [logs, setLogs] = useState<SecurityLogs | null>(null);
  const [logSource, setLogSource] = useState<SecurityLogSource>('crowdsec');
  const [loading, setLoading] = useState(true);
  const [safeIp, setSafeIp] = useState('');
  const [safeComment, setSafeComment] = useState('');
  const [busyIp, setBusyIp] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    const data = await securityService.getStatus();
    setStatus(data);
  }, []);

  const loadLogs = useCallback(async (source: SecurityLogSource) => {
    const data = await securityService.getLogs(source);
    setLogs(data);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadStatus(), loadLogs(logSource)]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || t('sysAdmin.security.loadError'));
    } finally {
      setLoading(false);
    }
  }, [loadLogs, loadStatus, logSource, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const bannedRows = useMemo(() => {
    const rows: { jail: string; ip: string }[] = [];
    for (const jail of status?.fail2ban.jails ?? []) {
      for (const ip of jail.banned ?? []) {
        rows.push({ jail: jail.name, ip });
      }
    }
    return rows;
  }, [status]);

  const handleUnban = async (ip: string) => {
    setBusyIp(ip);
    try {
      await securityService.unban(ip);
      toast.success(t('sysAdmin.security.unbanOk'));
      await loadStatus();
    } catch {
      toast.error(t('sysAdmin.security.unbanError'));
    } finally {
      setBusyIp(null);
    }
  };

  const handleAddSafe = async () => {
    if (!safeIp.trim()) return;
    setBusyIp(safeIp);
    try {
      await securityService.addSafeIp(safeIp.trim(), safeComment.trim() || undefined);
      toast.success(t('sysAdmin.security.safeAddOk'));
      setSafeIp('');
      setSafeComment('');
      await loadStatus();
    } catch {
      toast.error(t('sysAdmin.security.safeAddError'));
    } finally {
      setBusyIp(null);
    }
  };

  const handleRemoveSafe = async (id: number, ip: string) => {
    setBusyIp(ip);
    try {
      await securityService.removeSafeIp(id);
      toast.success(t('sysAdmin.security.safeRemoveOk'));
      await loadStatus();
    } catch {
      toast.error(t('sysAdmin.security.safeRemoveError'));
    } finally {
      setBusyIp(null);
    }
  };

  const changeLogSource = async (source: SecurityLogSource) => {
    setLogSource(source);
    try {
      await loadLogs(source);
    } catch {
      toast.error(t('sysAdmin.security.loadError'));
    }
  };

  return (
    <div className="p-6 space-y-6 text-textDefault">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PageBackButton />
          <ShieldCheckIcon className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">{t('sysAdmin.security.title')}</h1>
            <p className="text-sm text-textSecondary">{t('sysAdmin.security.description')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded hover:bg-bgLight"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('common.refresh')}
        </button>
      </div>

      {status && !status.hostctl && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
          {t('sysAdmin.security.hostctlMissing')}
        </div>
      )}

      {status ? (
        <CrowdSecEventsTable
          events={status.crowdsec.events ?? []}
          localBanCount={status.crowdsec.localBanCount ?? 0}
          installed={Boolean(status.crowdsec.installed)}
          active={Boolean(status.crowdsec.active)}
          busyIp={busyIp}
          onUnban={(ip) => void handleUnban(ip)}
        />
      ) : null}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-border rounded-lg p-4 bg-bgElevated">
          <h2 className="font-medium mb-2">{t('sysAdmin.security.fail2ban')}</h2>
          <p className="text-sm text-textSecondary mb-3">
            {status?.fail2ban.active ? t('sysAdmin.security.active') : t('sysAdmin.security.inactive')}
          </p>
          {bannedRows.length === 0 ? (
            <p className="text-sm text-textTertiary">{t('sysAdmin.security.noBans')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-textSecondary text-xs border-b border-border">
                  <th className="py-1">{t('sysAdmin.security.colIp')}</th>
                  <th className="py-1">{t('sysAdmin.security.colJail')}</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {bannedRows.map((row) => (
                  <tr key={`${row.jail}-${row.ip}`} className="border-b border-border">
                    <td className="py-1.5 font-mono">{row.ip}</td>
                    <td className="py-1.5">{row.jail}</td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        disabled={busyIp === row.ip}
                        onClick={() => void handleUnban(row.ip)}
                        className="text-primary text-xs underline disabled:opacity-50"
                      >
                        {t('sysAdmin.security.unban')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-border rounded-lg p-4 bg-bgElevated">
          <h2 className="font-medium mb-2">{t('sysAdmin.security.safeList')}</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              value={safeIp}
              onChange={(e) => setSafeIp(e.target.value)}
              placeholder="192.0.2.10"
              className="px-3 py-1.5 border border-border rounded text-sm bg-bgBase font-mono"
            />
            <input
              value={safeComment}
              onChange={(e) => setSafeComment(e.target.value)}
              placeholder={t('sysAdmin.security.comment')}
              className="px-3 py-1.5 border border-border rounded text-sm bg-bgBase flex-1 min-w-[8rem]"
            />
            <button
              type="button"
              onClick={() => void handleAddSafe()}
              disabled={!safeIp.trim() || busyIp === safeIp.trim()}
              className="px-3 py-1.5 bg-primary text-white text-sm rounded disabled:opacity-50"
            >
              {t('sysAdmin.security.addSafe')}
            </button>
          </div>
          {(status?.safeIps ?? []).length === 0 ? (
            <p className="text-sm text-textTertiary">{t('sysAdmin.security.noSafe')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-textSecondary text-xs border-b border-border">
                  <th className="py-1">{t('sysAdmin.security.colIp')}</th>
                  <th className="py-1">{t('sysAdmin.security.comment')}</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {(status?.safeIps ?? []).map((row) => (
                  <tr key={row.id} className="border-b border-border">
                    <td className="py-1.5 font-mono">{row.ip_address}</td>
                    <td className="py-1.5 text-textSecondary">{row.comment || '–'}</td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        disabled={busyIp === row.ip_address}
                        onClick={() => void handleRemoveSafe(row.id, row.ip_address)}
                        className="text-red-600 text-xs underline disabled:opacity-50"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="border border-border rounded-lg p-4 bg-bgElevated">
        <div className="flex flex-wrap gap-2 mb-3">
          {LOG_SOURCES.map((source) => (
            <button
              key={source}
              type="button"
              onClick={() => void changeLogSource(source)}
              className={`px-3 py-1 text-sm rounded border ${
                logSource === source
                  ? 'bg-primary text-white border-primary'
                  : 'border-border hover:bg-bgLight'
              }`}
            >
              {t(`sysAdmin.security.log.${source}`)}
            </button>
          ))}
        </div>
        {loading && !logs ? (
          <p className="text-sm text-textSecondary">{t('common.loading')}</p>
        ) : logs && !logs.available ? (
          <p className="text-sm text-textTertiary">{t('sysAdmin.security.noLogFile')}</p>
        ) : (
          <pre className="text-xs font-mono bg-bgBase border border-border rounded p-3 overflow-auto max-h-[28rem] whitespace-pre-wrap">
            {(logs?.lines ?? []).join('\n') || t('sysAdmin.security.emptyLog')}
          </pre>
        )}
      </div>
    </div>
  );
}
